import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, MapPin, Save, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/client';

/**
 * Delivery address entry, with suggestions from the saved address book and from
 * Google Places, plus a check against Shippo and Google before anything is
 * bought.
 *
 * Google is reached through our own API rather than by loading their SDK into
 * the page. The Base44 build fetched the Maps key to the browser and loaded the
 * script directly, which put a billable key in page source. Proxying keeps the
 * key server-side and means the page loads no third-party script at all.
 */
export default function AddressAutocomplete({ formData, setFormData, onSave, hideSaveButton }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(crypto.randomUUID());

  const applySuggestion = useCallback(
    (address) => {
      setFormData((prev) => ({
        ...prev,
        address1: address.address1,
        address2: address.address2 || prev.address2,
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country || prev.country || 'US',
      }));
      toast.success('Address corrected');
    },
    [setFormData],
  );

  const verification = useAddressCheck(formData);

  const { data: savedAddresses = [] } = useQuery({
    queryKey: ['deliveryAddresses'],
    queryFn: () => api.entities.DeliveryAddress.list(),
  });

  const { data: mapsStatus } = useQuery({
    queryKey: ['mapsStatus'],
    queryFn: () => api.request('/maps/status'),
    staleTime: Infinity,
  });

  const autocompleteAvailable = mapsStatus?.available ?? false;

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleStreetChange = (value) => {
    setFormData((prev) => ({ ...prev, address1: value }));

    clearTimeout(debounceRef.current);

    // Google's minimum useful prefix; querying on one or two characters just
    // spends quota on noise.
    if (!autocompleteAvailable || value.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const result = await api.request('/maps/autocomplete', {
          method: 'POST',
          body: { input: value.trim(), sessionToken: sessionTokenRef.current },
        });
        setSuggestions(result.suggestions ?? []);
      } catch {
        // A failed lookup must not block typing an address by hand.
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);
  };

  const handleSelectSuggestion = async (placeId) => {
    setSuggestions([]);
    try {
      const address = await api.request(
        `/maps/places/${encodeURIComponent(placeId)}?sessionToken=${sessionTokenRef.current}`,
      );

      setFormData((prev) => ({
        ...prev,
        address1: address.address1,
        address2: address.address2 || '',
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country || 'US',
      }));

      // Google bills a lookup as one session from keystrokes through selection;
      // a fresh token starts the next one.
      sessionTokenRef.current = crypto.randomUUID();
      toast.success('Address filled in');
    } catch (error) {
      toast.error(error.message ?? 'Could not load that address');
    }
  };

  const handleSelectSaved = (address) => {
    setFormData((prev) => ({
      ...prev,
      name: address.name,
      address1: address.address1,
      address2: address.address2 ?? '',
      city: address.city,
      state: address.state,
      zip: address.zip,
      country: address.country ?? 'US',
      phone: address.phone ?? '',
      email: address.email ?? '',
    }));
    setShowSaved(false);
    toast.success('Loaded saved address');
  };

  const matchingSaved = formData.name.trim()
    ? savedAddresses
        .filter((address) => {
          const term = formData.name.toLowerCase();
          return (
            address.name.toLowerCase().includes(term) ||
            address.address1.toLowerCase().includes(term) ||
            address.city.toLowerCase().includes(term)
          );
        })
        .slice(0, 8)
    : [];

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="recipient-name">Recipient name</Label>
        <div className="relative">
          <Input
            id="recipient-name"
            value={formData.name}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, name: e.target.value }));
              setShowSaved(e.target.value.length > 0);
            }}
            onFocus={() => setShowSaved(formData.name.length > 0)}
            // Delayed so a click on a suggestion registers before the list closes.
            onBlur={() => setTimeout(() => setShowSaved(false), 200)}
            placeholder="Start typing to search saved addresses…"
            autoComplete="off"
          />
          {showSaved && matchingSaved.length > 0 && (
            <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
              {matchingSaved.map((address) => (
                <li key={address.id}>
                  <button
                    type="button"
                    onMouseDown={() => handleSelectSaved(address)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b last:border-b-0 transition-colors"
                  >
                    <span className="block font-semibold text-slate-900">{address.name}</span>
                    <span className="block text-sm text-slate-600">{address.address1}</span>
                    <span className="block text-sm text-slate-500">
                      {address.city}, {address.state} {address.zip}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="recipient-phone">Phone (optional)</Label>
          <Input
            id="recipient-phone"
            type="tel"
            value={formData.phone ?? ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
            placeholder="(212) 555-0188"
            autoComplete="off"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="recipient-email">Email (optional)</Label>
          <Input
            id="recipient-email"
            type="email"
            value={formData.email ?? ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            placeholder="customer@example.com"
            autoComplete="off"
          />
        </div>
      </div>

      {/*
        Said once, here, rather than as a red error after a failed purchase.
        USPS ground rarely needs either; UPS and FedEx usually do, and finding
        that out at the moment of buying is the annoying way to learn it.
      */}
      <p className="text-xs text-slate-500 -mt-2">
        UPS and FedEx usually require a phone number, and some services require an email. USPS
        ground normally needs neither.
      </p>

      <div className="space-y-2">
        <Label htmlFor="recipient-address1">Street address</Label>
        <div className="relative">
          <Input
            id="recipient-address1"
            value={formData.address1}
            onChange={(e) => handleStreetChange(e.target.value)}
            onBlur={() => setTimeout(() => setSuggestions([]), 200)}
            placeholder={autocompleteAvailable ? 'Start typing an address…' : '123 Main Street'}
            autoComplete="off"
          />
          {loadingSuggestions && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
          )}
          {suggestions.length > 0 && (
            <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
              {suggestions.map((suggestion) => (
                <li key={suggestion.placeId}>
                  <button
                    type="button"
                    onMouseDown={() => handleSelectSuggestion(suggestion.placeId)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b last:border-b-0 transition-colors flex items-start gap-2"
                  >
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <span>
                      <span className="block text-sm font-medium text-slate-900">
                        {suggestion.mainText}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {suggestion.secondaryText}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {autocompleteAvailable ? (
          <p className="text-xs text-slate-500">City, state, and ZIP fill in automatically.</p>
        ) : (
          <p className="text-xs text-slate-500">
            Address autocomplete is off — no Google Maps key is configured on the server.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="recipient-address2">Address line 2 (optional)</Label>
        <Input
          id="recipient-address2"
          value={formData.address2}
          onChange={(e) => setFormData((prev) => ({ ...prev, address2: e.target.value }))}
          placeholder="Apt, suite, floor…"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2 col-span-2">
          <Label htmlFor="recipient-city">City</Label>
          <Input
            id="recipient-city"
            value={formData.city}
            onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="recipient-state">State</Label>
          <Input
            id="recipient-state"
            value={formData.state}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, state: e.target.value.toUpperCase() }))
            }
            placeholder="NY"
            maxLength={2}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="recipient-zip">ZIP code</Label>
          <Input
            id="recipient-zip"
            value={formData.zip}
            onChange={(e) => setFormData((prev) => ({ ...prev, zip: e.target.value }))}
            placeholder="10001"
          />
        </div>
      </div>

      <AddressVerdict verification={verification} onApply={applySuggestion} />

      {!hideSaveButton && (
        <Button type="button" variant="outline" onClick={onSave} className="w-full gap-2">
          <Save className="w-4 h-4" />
          Save address for future use
        </Button>
      )}
    </div>
  );
}

/** Nothing to check until all four parts of a US address are present. */
function isComplete(address) {
  return (
    address.address1.trim().length > 0 &&
    address.city.trim().length > 0 &&
    /^[A-Za-z]{2}$/.test(address.state.trim()) &&
    /^\d{5}(-\d{4})?$/.test(address.zip.trim())
  );
}

function signatureOf(address) {
  return [address.address1, address.address2, address.city, address.state, address.zip]
    .map((part) => (part ?? '').trim().toLowerCase())
    .join('|');
}

/**
 * Checks the address as soon as it is complete, rather than behind a button.
 *
 * A button labelled "verify" gets pressed by the people who already type
 * carefully. Running it automatically is the only version that catches the
 * hurried Friday-afternoon typo, which is the whole point. Debounced and
 * deduplicated by content, so it is one request per finished address, not one
 * per keystroke.
 */
function useAddressCheck(formData) {
  // Empty until there is something worth checking, which also makes the effect
  // below fire on the address's *content* rather than on every render.
  const signature = isComplete(formData) ? signatureOf(formData) : '';

  const [checked, setChecked] = useState({ signature: '', result: null });
  const [checking, setChecking] = useState(false);

  // Read inside the effect without making it a dependency — every field it
  // needs is already folded into the signature.
  const formRef = useRef(formData);
  formRef.current = formData;

  // Guards against an earlier, slower response overwriting a newer one.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!signature || signature === checked.signature) return undefined;

    const timer = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      const address = formRef.current;
      setChecking(true);

      try {
        const result = await api.request('/addresses/validate', {
          method: 'POST',
          body: {
            name: address.name?.trim() || undefined,
            address1: address.address1.trim(),
            address2: address.address2?.trim() || '',
            city: address.city.trim(),
            state: address.state.trim().toUpperCase(),
            zip: address.zip.trim(),
            country: address.country || 'US',
          },
        });

        if (requestId !== requestIdRef.current) return;
        setChecked({ signature, result });
      } catch {
        // A checker that is down must never stop someone shipping. Say nothing
        // and let them carry on; the carrier is the real arbiter anyway.
        if (requestId !== requestIdRef.current) return;
      } finally {
        if (requestId === requestIdRef.current) setChecking(false);
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [signature, checked.signature]);

  if (!signature) return { status: 'idle', result: null };
  if (checking) return { status: 'checking', result: null };
  // Editing back to an address already checked shows that answer again rather
  // than re-spending a lookup on it.
  if (checked.signature === signature) return { status: 'done', result: checked.result };
  return { status: 'stale', result: null };
}

function AddressVerdict({ verification, onApply }) {
  const { status, result } = verification;

  if (status === 'idle') return null;

  if (status === 'checking') {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Checking this address…
      </p>
    );
  }
  if (status === 'stale' || !result) return null;

  if (!result.isValid) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1">
        <p className="flex items-center gap-2 text-sm font-semibold text-red-900">
          <XCircle className="w-4 h-4" />
          The carrier will not accept this address
        </p>
        {result.messages?.map((message) => (
          <p key={message} className="text-sm text-red-800 pl-6">
            {message}
          </p>
        ))}
      </div>
    );
  }

  /*
    A missing unit number gets its own treatment, above the generic suggestion.
    It is the most common real defect in a delivery address and the only one
    where the fix is something the operator has to go and ask for — a corrected
    street spelling can be applied with a button, "which apartment?" cannot.

    Read from Shippo's own messages: "More information, such as an apartment or
    suite number, may give a more specific address." That was previously only
    shown when the address was outright invalid, so the single most useful
    warning the app already had was invisible in exactly the case it mattered.
  */
  const shippoWantsUnit = (result.messages ?? []).some((message) =>
    /apartment|suite|secondary|more specific address/i.test(message),
  );

  if (shippoWantsUnit) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertTriangle className="w-4 h-4" />
          This address needs an apartment or suite number
        </p>
        {(result.messages ?? []).map((note) => (
          <p key={note} className="text-sm text-amber-900 pl-6">
            {note}
          </p>
        ))}
        <p className="text-sm text-amber-800 pl-6">
          The building is deliverable — without the unit this may come back.
        </p>
        {result.hasSuggestion && result.suggested && (
          <div className="pl-6">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onApply(result.suggested)}
              className="border-amber-300 bg-white hover:bg-amber-100"
            >
              Use the standardised address
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (result.hasSuggestion && result.suggested) {
    const s = result.suggested;
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertTriangle className="w-4 h-4" />
          Suggested correction
        </p>
        <p className="text-sm text-amber-900 pl-6">
          {s.address1}
          {s.address2 ? `, ${s.address2}` : ''}
          <br />
          {s.city}, {s.state} {s.zip}
        </p>
        <div className="pl-6">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onApply(s)}
            className="border-amber-300 bg-white hover:bg-amber-100"
          >
            Use this address
          </Button>
        </div>
      </div>
    );
  }

  // Google not confirming is worth mentioning but not worth a warning colour —
  // rural routes and new construction are routinely unknown to it, and the
  // carrier has already said it will deliver here.
  const googleNotes = result.google?.checked && !result.google.confirmed ? result.google.notes : [];

  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-1">
      <p className="flex items-center gap-2 text-sm font-semibold text-green-900">
        <CheckCircle2 className="w-4 h-4" />
        {result.google?.confirmed ? 'Address checked and confirmed by Google' : 'Address checked'}
      </p>
      {googleNotes.map((note) => (
        <p key={note} className="text-sm text-green-800 pl-6">
          {note} — worth a second look.
        </p>
      ))}
    </div>
  );
}
